import React, { useState, useEffect } from 'react';
import ReactFlow, {
  Node,
  Edge,
  Controls,
  Background,
  MarkerType,
  Panel,
  Handle,
  Position,
  ConnectionMode
} from 'reactflow';
import 'reactflow/dist/style.css';
import { Box, Typography, IconButton, CircularProgress, Tooltip, Paper } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { getReviewEntity } from '../../services/api';

// Custom node component to display entity information with handles and hover tooltip
const EntityNode: React.FC<any> = ({ data }) => {
  const [entityDetails, setEntityDetails] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const handleMouseEnter = async () => {
    if (!entityDetails && !loading) {
      setLoading(true);
      try {
        const response = await getReviewEntity(data.entityId);
        setEntityDetails(response.data);
      } catch (error) {
        console.error('Error fetching entity details:', error);
        setEntityDetails({ error: 'Failed to load details' });
      } finally {
        setLoading(false);
      }
    }
    setTooltipOpen(true);
  };

  const handleMouseLeave = () => {
    setTooltipOpen(false);
  };

  const renderTooltipContent = () => {
    if (loading) {
      return (
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="body2">Loading details...</Typography>
        </Box>
      );
    }

    if (entityDetails?.error) {
      return (
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" color="error">
            {entityDetails.error}
          </Typography>
        </Box>
      );
    }

    if (entityDetails) {
      return (
        <Paper elevation={3} sx={{ p: 2, maxWidth: 300 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
            {entityDetails.name}
          </Typography>
          <Box sx={{ mb: 1 }}>
            <Typography variant="caption" color="text.secondary">Description:</Typography>
            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
              {entityDetails.description || 'No description'}
            </Typography>
          </Box>
          <Box sx={{ mb: 1 }}>
            <Typography variant="caption" color="text.secondary">Organization Code:</Typography>
            <Typography variant="body2">
              {entityDetails.org_entity_code || 'Not specified'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Owner:</Typography>
            <Typography variant="body2">
              {entityDetails.owner_display_name || entityDetails.owner_name || 'Not assigned'}
            </Typography>
          </Box>
        </Paper>
      );
    }

    return null;
  };

  return (
    <Tooltip
      title={renderTooltipContent()}
      open={tooltipOpen}
      onOpen={handleMouseEnter}
      onClose={handleMouseLeave}
      placement="top"
      arrow
      PopperProps={{
        sx: {
          '& .MuiTooltip-tooltip': {
            backgroundColor: 'transparent',
            padding: 0,
            maxWidth: 'none',
          }
        }
      }}
    >
      <div 
        className="entity-node" 
        style={{ 
          padding: '8px',
          borderRadius: '5px',
          border: data.isPrimary ? '2px solid #1976d2' : '1px solid #ccc',
          background: data.isPrimary ? '#e3f2fd' : '#f5f5f5',
          width: '200px',
          overflow: 'hidden',
          textAlign: 'center',
          position: 'relative',
          cursor: 'pointer'
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* Add bottom handle for connections from this node */}
        <Handle
          type="source"
          position={Position.Bottom}
          id="source-bottom"
          style={{ background: '#555', bottom: 0 }}
        />
        
        {/* Add top handle for connections to this node */}
        <Handle
          type="target"
          position={Position.Top}
          id="target-top"
          style={{ background: '#555', top: 0 }}
        />
        
        <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{data.label}</div>
        <div style={{ fontSize: '0.8rem', color: '#666' }}>{data.entityType}</div>
      </div>
    </Tooltip>
  );
};

// Define node types for ReactFlow
const nodeTypes = {
  entityNode: EntityNode,
};

interface EntityBase {
  id: number;
  name: string;
  review_entity_type_id: number;
  review_entity_type?: {
    id: number;
    name: string;
    display_label: string;
    category: string;
    description: string;
  };
  parent_ids?: number[];
}

interface EntityNode extends EntityBase {
  parents?: EntityBase[];
  description?: string;
  org_entity_code?: string;
  reviewers?: number[];
  reviewees?: number[];
  owner?: number;
  owner_name?: string;
  owner_display_name?: string;
  status?: string;
  valid_from?: string;
  valid_till?: string;
  created_by?: number;
  updated_by?: number;
  created_at?: string;
  updated_at?: string;
  review_attributes?: any[];
  attributes?: any[];
  reviewer_names?: string[];
  reviewee_names?: string[];
}

interface EntityHierarchyVisualizationProps {
  entityId: number;
  onClose: () => void;
}

interface NodeData {
  id: number;
  name: string;
  entityType: string;  // Will store display_label from review_entity_type
  x: number;
  y: number;
  isPrimary: boolean;
  parentId?: number;
}

const EntityHierarchyVisualization: React.FC<EntityHierarchyVisualizationProps> = ({ entityId, onClose }) => {
  const [nodes, setNodes] = useState<Node[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [allNodeData, setAllNodeData] = useState<NodeData[]>([]);

  // Fetch all entity data and organize them
  useEffect(() => {
    // Clear previous state
    setNodes([]);
    setEdges([]);
    setAllNodeData([]);
    setLoading(true);
    setError('');
    
    // Track processed entities to avoid duplicates
    const processedEntityIds = new Set<number>();
    
    // Start collecting data from the root entity
    collectEntityData(entityId, 0, 0, 400, undefined, processedEntityIds);
  }, [entityId]);

  // Once we have all entity data, transform it into nodes and edges
  useEffect(() => {
    if (allNodeData.length > 0 && loading === false) {
      console.log("Building visualization from collected data:", allNodeData);
      
      // Process entity data to properly position all entities
      const processedData = organizeEntityLayout(allNodeData);
      
      // Build the visualization with the processed data
      buildVisualization(processedData);
    }
  }, [allNodeData, loading]);
  
  // Organize entity layout to ensure parents without parents are at the top
  const organizeEntityLayout = (nodeData: NodeData[]) => {
    // Create a map to track which entities are referenced as parents
    const parentMap = new Map<number, boolean>();
    
    // Mark all entities that are referenced as parents
    nodeData.forEach(node => {
      if (node.parentId) {
        parentMap.set(node.parentId, true);
      }
    });
    
    // Find the top-level entities (those not referenced as children by any other entity)
    const topLevelEntities = nodeData.filter(node => 
      node.id !== entityId && !nodeData.some(n => n.parentId === node.id)
    );
    
    // If we found any orphaned parents, adjust their positions to be at the top
    let processed = [...nodeData];
    if (topLevelEntities.length > 0) {
      // Move orphaned parents to the top level in y-coordinate
      const topY = Math.min(...nodeData.map(n => n.y)) - 150;
      
      processed = nodeData.map(node => {
        if (topLevelEntities.some(e => e.id === node.id)) {
          // Spread these nodes horizontally to avoid overlap
          const index = topLevelEntities.findIndex(e => e.id === node.id);
          const totalWidth = topLevelEntities.length * 250;
          const startX = -totalWidth / 2 + 125;
          
          return {
            ...node,
            y: topY,
            x: startX + (index * 250)
          };
        }
        return node;
      });
      
      console.log("Adjusted layout for top-level entities:", topLevelEntities.map(e => e.id));
    }
    
    return processed;
  };

  // Collect all entity data recursively
  const collectEntityData = async (
    id: number, 
    level: number = 0, 
    horizontalPos: number = 0, 
    verticalPos: number = 500, // Increased vertical spacing
    parentId?: number,
    processedIds: Set<number> = new Set<number>()
  ) => {
    try {
      // Skip if we've reached a reasonable limit for levels or if we've already processed this entity
      if (level > 5 || processedIds.has(id)) return;
      
      // Mark this entity as processed
      processedIds.add(id);

      // Fetch the entity data
      const response = await getReviewEntity(id);
      const entity = response.data as EntityNode;
      console.log(`Loaded entity ${id}:`, entity);

      // Add this entity to our collection
      const nodeData: NodeData = {
        id,
        name: entity.name,
        entityType: entity.review_entity_type?.display_label || 'Unknown',
        x: horizontalPos,
        y: verticalPos,
        isPrimary: id === entityId,
        parentId
      };

      setAllNodeData(prev => {
        // Check if this entity is already in our collection
        if (!prev.some(node => node.id === id)) {
          return [...prev, nodeData];
        }
        return prev;
      });

      // Process parent entities
      if (entity.parents && entity.parents.length > 0) {
        const parentCount = entity.parents.length;
        // Increase spacing between nodes to prevent overlap
        const parentSpacing = Math.max(250, 600 / parentCount);
        const startX = horizontalPos - ((parentCount - 1) * parentSpacing) / 2;
        const parentY = verticalPos - 150; // Increased vertical distance between levels

        // Collect data for each parent
        for (let i = 0; i < parentCount; i++) {
          const parent = entity.parents[i];
          if (!parent || !parent.id) continue;

          // Skip if we've already processed this parent
          if (!processedIds.has(parent.id)) {
            // Recursively collect data for this parent
            // Add slight horizontal jitter if there are many parents to reduce chance of overlap
            const jitter = parentCount > 3 ? (Math.random() * 40 - 20) : 0;
            const parentX = startX + (i * parentSpacing) + jitter;
            await collectEntityData(parent.id, level + 1, parentX, parentY, id, processedIds);
          }
        }
      }

      // Mark loading as complete when we've processed the root entity
      if (id === entityId) {
        setLoading(false);
      }
    } catch (error) {
      console.error(`Error fetching entity ${id}:`, error);
      setError(`Failed to load entity hierarchy. Error: ${error}`);
      setLoading(false);
    }
  };

  // Build the visualization from the collected data
  const buildVisualization = (processedData: NodeData[]) => {
    // Use a Map to deduplicate nodes by ID
    const nodeMap = new Map<string, Node>();
    
    // Transform node data into ReactFlow nodes with deduplication
    processedData.forEach(data => {
      const nodeId = `entity-${data.id}`;
      if (!nodeMap.has(nodeId)) {
        nodeMap.set(nodeId, {
          id: nodeId,
          type: 'entityNode',
          position: { x: data.x, y: data.y },
          data: {
            label: data.name,
            entityType: data.entityType,
            isPrimary: data.isPrimary,
            entityId: data.id
          }
        });
      }
    });
    const flowNodes = Array.from(nodeMap.values());
    console.log("Final ReactFlow nodes:", flowNodes);
    setNodes(flowNodes);
    
    // Use a Map to deduplicate edges by ID
    const edgeMap = new Map<string, Edge>();
    
    console.log("constructing edges");
    // Create edges between related nodes
    processedData.forEach(data => {
      if (data.parentId) {
        // Create a unique edge ID
        const edgeId = `edge-${data.id}-${data.parentId}`;
        
        // Only add if this edge doesn't already exist
        if (!edgeMap.has(edgeId)) {
          // NOTE: We've reversed source and target to match Reactflow's expectations
          // The child is now the source, and the parent is the target (edges point up)
          const edge: Edge = {
            id: edgeId,
            source: `entity-${data.id}`,      // Child is source
            target: `entity-${data.parentId}`,  // Parent is target
            sourceHandle: 'source-bottom',     // Connect from bottom of child
            targetHandle: 'target-top',       // Connect to top of parent
            type: 'straight',                 // Use straight edges for reliable rendering
            animated: false,                  // No animation as requested
            style: { stroke: '#2196f3', strokeWidth: 2 },
            markerEnd: {
              type: MarkerType.ArrowClosed,
              color: '#2196f3',
            }
          };
          edgeMap.set(edgeId, edge);
        }
      }
    });
    
    // Convert Maps to arrays
    
    const flowEdges = Array.from(edgeMap.values());
    console.log("Final ReactFlow edges:", flowEdges);
    // Update the state with all nodes and edges at once
    
    setEdges(flowEdges);
  };

  return (
    <Box sx={{ 
      position: 'fixed', 
      top: 0, 
      left: 0, 
      right: 0, 
      bottom: 0, 
      backgroundColor: 'rgba(0, 0, 0, 0.6)', 
      zIndex: 1300,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    }}>
      <Box sx={{ 
        backgroundColor: 'white',
        borderRadius: '8px',
        width: '90%',
        height: '90%',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}>
        <Box sx={{ 
          padding: '16px',
          borderBottom: '1px solid #ddd',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="h6">
            Entity Hierarchy Visualization
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        
        <Box sx={{ 
          flexGrow: 1, 
          position: 'relative', 
          width: '100%', 
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}>
          {loading ? (
            <CircularProgress />
          ) : error ? (
            <Typography color="error">{error}</Typography>
          ) : (
            <ReactFlow
              nodes={nodes}
              edges={edges}
              nodeTypes={nodeTypes}
              fitView
              fitViewOptions={{ padding: 0.5 }}
              defaultViewport={{ x: 0, y: 0, zoom: 0.8 }}
              connectionLineStyle={{ stroke: '#2196f3', strokeWidth: 2 }}
              defaultEdgeOptions={{
                type: 'straight',
                animated: false,
                markerEnd: {
                  type: MarkerType.ArrowClosed,
                },
                style: { stroke: '#2196f3', strokeWidth: 2 },
              }}
              connectionMode={ConnectionMode.Loose}
              nodesConnectable={false}
              elementsSelectable={true}       // Allow selecting elements
              nodesDraggable={true}          // Allow nodes to be dragged
              zoomOnScroll={true}            // Allow zoom for better navigation
              panOnScroll={true}
              minZoom={0.5}
              maxZoom={1.5}
            >
              <Controls />
            </ReactFlow>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default EntityHierarchyVisualization;
